
import { Ref, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";
import AttributeSchemaEditorUtils, { AttributeOptionActionEnum, AttributeSchemaAdminMode, AttributeSchemaFormType, reservedWords, schemaTypeText } from "../AttributeSchemaEditorUtils";
import { useFormContext, useWatch } from "react-hook-form";
import { Alert, FormLabel, MenuItem, Stack } from "@mui/material";
import AttributeValueOptions, { AttributeValueOptionType } from "./AttributeValueOptions";
import { isMobile, isTablet } from "react-device-detect";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useAttributesUsage } from "../../../queries/attributes/UseAttributesUsage";
import { useOrganizations } from "../../../queries/organizations/UseOrganizations";
import { AttributeSchemaTypeEnum } from "../../../services/attribute-data-service/Models";
import { BaseActions } from "../../action-menu/ActionMenuItem";
import { ContentContainer } from "../../content-container/ContentContainer";
import { ContentSection } from "../../content-section/ContentSection";
import FormCheckbox from "../../forms/form-checkbox/FormCheckbox";
import FormInput from "../../forms/form-input/FormInput";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import FormProperty from "../../forms/form-property/FormProperty";
import FormSelect from "../../forms/form-select/FormSelect";
import { IeLynxObject } from "../../../common/models/eLynxObject";

export type AttributeSchemaPropertiesProps = {
    organizationID: number,
    targetObject?: IeLynxObject
};

export type AttributeSchemePropertiesType = {
    executeAction: (action: AttributeOptionActionEnum | BaseActions) => void;
};

function AttributeSchemaProperties({
    organizationID,
    targetObject

}: AttributeSchemaPropertiesProps, ref: Ref<unknown> | undefined) { 

    const isPhone = isMobile && !isTablet;    

    const valueOptionsRef = useRef<AttributeValueOptionType>(undefined);
    const { t } = useTranslation();

    const { data: organizations } = useQueryNotifications(useOrganizations)({});
    const organization = organizations?.find(o => o.id === organizationID);

    const {data} = useQueryNotifications(useAttributesUsage)({
        groupID: organization?.peerGroupID ?? -1,
        organizationID: organizationID,
    });

    const {
        getValues,
        watch,
        trigger,
        control,
        reset,
        //setValue,
        resetField
    } = useFormContext<AttributeSchemaFormType>();    
   

    useImperativeHandle<unknown, AttributeSchemePropertiesType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );
    
    const attributeSchemaForm = useWatch({ control: control }) as AttributeSchemaFormType;

    const [
        name,
        schemaType,
        includeDevices,
        includeTanks,
        includeTankBatteries,
        includeWells
    ] = watch([
        'name',
        'schemaType',
        'includeDevices',
        'includeTanks',
        'includeTankBatteries',
        'includeWells'
    ]);

    //const mode = (attributeSchemaForm?.id ?? 0) <= 0 ? AttributeSchemaAdminMode.Add : AttributeSchemaAdminMode.Edit;
    const mode = attributeSchemaForm?.id === -1 ? AttributeSchemaAdminMode.Add : AttributeSchemaAdminMode.Edit;

    const deviceSelected = attributeSchemaForm.devices.find(o => o.selected === true) !== undefined;
    const tankSelected = attributeSchemaForm.tanks.find(o => o.selected === true) !== undefined;
    const wellSelected = attributeSchemaForm.wells.find(o => o.selected === true) !== undefined;
    const tankBatterySelected = attributeSchemaForm.tankBatteries.find(o => o.selected === true) !== undefined;

    const includeAnyObjects = includeDevices || includeTanks || includeTankBatteries || includeWells;


    function executeAction(action: AttributeOptionActionEnum | BaseActions) {
        valueOptionsRef.current?.executeAction(action);
    }


    function resetSelected(name: 'devices' | 'tanks' | 'wells' | 'tankBatteries') {

        const objectAttributes = getValues(name);
        objectAttributes.forEach((_o, ndx) => resetField(`${name}.${ndx}.selected`, {defaultValue: false}));
    }


    function onSchemaTypeChange() {

        const value = getValues('schemaType');

        if (value !== AttributeSchemaTypeEnum.Numeric) {
            resetField('decimalDigits');
            resetField('wholeDigits');
        }

        if (value !== AttributeSchemaTypeEnum.PredefinedValue &&
            value !== AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) {
                resetField('valueOptions');
        }

        if (value === AttributeSchemaTypeEnum.Date) {
            const formValue = getValues();

            reset(AttributeSchemaEditorUtils.updateFormDateValues(formValue), {
                keepDirtyValues: true,
                keepDirty: true,
                keepIsValid: true
            });
        }

        resetSelected('devices');
        resetSelected('wells');
        resetSelected('tanks');
        resetSelected('tankBatteries');
    }

    const _validateName = (value: string): boolean => {

        return value.toString().trim().length <= 50;
    }

    const _validateNameUnique = (value: string): boolean => {

        return reservedWords.find(o => o.toLowerCase() === value.trim().toLowerCase()) === undefined &&
            data?.find(o => o.attributeSchemaID !== (attributeSchemaForm?.id ?? 0) && o.attributeName.toLowerCase() === value) === undefined;
    }

    const _validateRange = (value: string, min: number, max: number): boolean => {
        return +value > -1;
    }

    // execute on every render
    useEffect(() => {

        setTimeout(() => {
            let items: ('name')[] = [];

            if ((name?.trim().length ?? 0) === 0) {
                items.push('name');
            }

            if (items.length) {
                trigger(items);
            }

        }, 1);
    });

   return (

        <ContentContainer>
            <ContentSection sx={{
                padding: 1,
                overflowY: 'auto'
            }}>

            {/* {!isPhone && (schemaType === AttributeSchemaTypeEnum.PredefinedValue || schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) ? 
                <Stack position='absolute' top={theme.spacing(1)} right={theme.spacing(3)}>
                    <Button onClick={() => executeAction(AttributeOptionActionEnum.Add)}>
                        {t('attributeSchema.addOption')}
                    </Button>
                </Stack>
                : 
                null 
            } */}
            
                <FormPropertySection label={t('properties.standard')}>

                    <FormProperty>
                        <FormInput
                            name="name"
                            label={t('properties.name')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                                marginBottom: 1,
                            }}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('attributeSchema.nameRequired')
                                },
                                validate: (value: string) => !_validateName(value) ?
                                    t('attributeSchema.nameLength') : 
                                !_validateNameUnique(value) ?
                                    t('attributeSchema.nameUnique') : 
                                true
                            }}

                        />
                    </FormProperty>

                    <FormProperty>
                        <FormSelect
                            id="schemaType"
                            name="schemaType"
                            label={t('attributeSchema.type')}
                            readOnly={mode === AttributeSchemaAdminMode.Edit}
                            sx={{ 
                                width: 1,
                                marginTop: 1,
                            }}
                            onChange={() => onSchemaTypeChange()}
                        >
                            <MenuItem  
                                disabled={true}
                                value={AttributeSchemaTypeEnum.None}>
                                {t('attributeSchema.typePrompt')}
                                
                            </MenuItem>
                            {schemaTypeText.map(o => (
                                <MenuItem key={o.Key} value={o.Key}>
                                    {o.Value}
                                </MenuItem>
                            ))}
                           
                        </FormSelect>

                    </FormProperty> 

                </FormPropertySection>

                <FormPropertySection label=''>

                    <FormCheckbox
                        name="allowGroupBy"
                        label={t('attributeSchema.allowGroupBy')}
                    />

                </FormPropertySection>


                {
                schemaType === AttributeSchemaTypeEnum.Numeric &&
                <FormPropertySection label=''>
                    <FormProperty>
                    <FormInput
                            name="wholeDigits"
                            label={t('attributeSchema.wholeNumberPlaces')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                            }}
                            numeric={true}
                            
                            decimals={0}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('setpointAdmin.valueRequired')
                                }, 
                                validate: (value: string) => !_validateRange(value, 0, 99) ?
                                t('attributeSchema.invalidRange', {min: 0, max: 99}) : 
                                true
        
                            }}
                    /> 
                    </FormProperty>

                    <FormProperty>

                        <FormInput
                            name="decimalDigits"
                            label={t('attributeSchema.decimalPlaces')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                            }}
                            numeric={true}
                            
                            decimals={0}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('attributeSchema.valueRequired')
                                }, 
                                validate: (value: string) => !_validateRange(value, 0, 28) ?
                                t('attributeSchema.invalidRange', {min: 0, max: 28}) : 
                                true
        
                            }}
                        /> 
                    </FormProperty>

                </FormPropertySection>
                }


                {
                targetObject === undefined && 
                <>
                    <FormPropertySection label=''>
                        <FormLabel>{t('attributeSchema.objectTypeInfo')}</FormLabel>                    
                    </FormPropertySection>        


                    <FormPropertySection label=''>

                        <Stack direction={isPhone ? 'column' : 'row'}
                            sx={{
                                marginTop: 1,
                                marginBottom: 1
                            }}>

                            {
                            attributeSchemaForm.devices.length > 0 && 
                            <FormCheckbox
                                name="includeDevices"
                                label={getObjectTypeName(eLynxObjectTypeEnum.Device, true)}
                                disabled={deviceSelected}                            
                            />
                            }


                            {
                            attributeSchemaForm.tanks.length > 0 && 
                            <FormCheckbox
                                name="includeTanks"
                                label={getObjectTypeName(eLynxObjectTypeEnum.Tank, true)}
                                disabled={tankSelected}                            
                            />
                            }

                            {
                            attributeSchemaForm.tankBatteries.length > 0 && 

                            <FormCheckbox
                                name="includeTankBatteries"
                                label={getObjectTypeName(eLynxObjectTypeEnum.TankBattery, true)}
                                disabled={tankBatterySelected}
                            />
                            }

                            {
                            attributeSchemaForm.wells.length > 0 && 

                            <FormCheckbox
                                name="includeWells"
                                label={getObjectTypeName(eLynxObjectTypeEnum.Well, true)}
                                disabled={wellSelected}
                                
                            />
                            }

                        </Stack>

                    </FormPropertySection>
                </>
                }

                {!includeAnyObjects && 

                    <Alert severity="error">
                        {t('attributeSchema.objectTypeRequired')}
                    </Alert>
                }


                {
                    (schemaType === AttributeSchemaTypeEnum.PredefinedValue || schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) &&

                        <AttributeValueOptions 
                            name='valueOptions' 
                            ref={valueOptionsRef}
                            readOnly={mode === AttributeSchemaAdminMode.Edit}
                        />
                }


            </ContentSection>        
        </ContentContainer>
        
    );
  }

  export default forwardRef(AttributeSchemaProperties);
